import { analytics } from '@/features/analytics/events'
import {
  useNectarProjectsUnauthorized,
  useProficiencyAssessmentsByLanguage,
  useProjectAccessRequestInfo,
  useRequestProjectAccess
} from '@/features/nectar/api'
import { NectarProject } from '@/features/nectar/api/models'
import { nectarProjectToMission } from '@/features/nectar/api/normalizer'
import { AnnotatorVerificationDialog } from '@/features/nectar/components/annotator-verification/AnnotatorVerificationDialog'
import { EnrollmentContent } from '@/features/nectar/components/EnrollmentContent'
import { routes } from '@/pages'
import { Button, Text } from '@/ui-kit'
import { formatNumber } from '@/utils'
import { Trans, useLingui } from '@lingui/react/macro'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMissionsUrl } from '../context/MissionsUrlProvider'
import { useMissionRedirect } from '../hooks/useMissionRedirect'
import { MissionLockedByLanguages } from './MissionLockedByLanguages'
import { WizardStep } from './WizardStep'

export const MissionOnboarding = ({ project, isAuthorized }: { project: NectarProject; isAuthorized: boolean }) => {
  const {
    id: projectId,
    name: title,
    description = '',
    metadata = { estimatedMinutes: null },
    availability: { reasons = [] },
    rules = '',
    whitelistEnabled,
    isUserWhitelisted
  } = project
  const estimatedMinutes = metadata?.estimatedMinutes ?? null
  const rewardConfig = { type: project.rewardConfig?.type ?? 'points', amount: project.totalReward ?? 0 }
  const { t } = useLingui()
  const { profileUrl, missionsUrl, assessmentUrl } = useMissionsUrl()

  const navigate = useNavigate()
  const { withRedirectToCurrent } = useMissionRedirect()
  const { data: publicProjects = { data: [] } } = useNectarProjectsUnauthorized()
  const assessmentMission = publicProjects.data
    .map((assessmentProject) => nectarProjectToMission(assessmentProject, missionsUrl))
    .find((assessmentProject) => assessmentProject.type === 'PROFICIENCY_ASSESSMENT')

  const {
    data: accessRequestInfo = {
      gated: false,
      requirements: { profileComplete: false, languageAssessmentComplete: false },
      status: 'NONE'
    },
    isLoading: isAccessRequestInfoLoading
  } = useProjectAccessRequestInfo(projectId, { enabled: isAuthorized })

  const { mutateAsync: requestAccess, isPending: isRequestingAccess } = useRequestProjectAccess()

  const profileCompleted = !isAccessRequestInfoLoading
    ? accessRequestInfo?.gated && !isUserWhitelisted
      ? accessRequestInfo?.requirements.profileComplete
      : reasons.some((reason) => reason.type === 'profile_required' && reason.completed)
    : false

  const assessmentCompleted = !isAccessRequestInfoLoading
    ? accessRequestInfo?.gated && !isUserWhitelisted
      ? accessRequestInfo?.requirements.languageAssessmentComplete
      : reasons.some((reason) => reason.type === 'language_assessment' && reason.completed)
    : false

  const { data: assessmentsByLanguage } = useProficiencyAssessmentsByLanguage('en', {
    enabled: isAuthorized && assessmentCompleted
  })
  const assessmentFinalResult = assessmentsByLanguage?.data.find((a) => a.finalResult)?.finalResult ?? null

  const goToLogin = () => {
    const returnTo = `${location.pathname}${location.search}`
    analytics.missionOnboardingStepCTA(projectId, 'Sign In')
    analytics.signInFromMissionOnBoarding(projectId)
    navigate(`${routes.login.path}?redirect=${encodeURIComponent(returnTo)}`)
  }

  const goToProfile = () => {
    navigate(withRedirectToCurrent(profileUrl))
  }

  const goToAssessment = () => {
    if (!assessmentMission) return
    navigate(withRedirectToCurrent(assessmentUrl.replace(':langCode', 'en')))
  }

  const hasAssessmentStep = !isAccessRequestInfoLoading
    ? accessRequestInfo?.gated && !isUserWhitelisted
      ? true
      : reasons.some((reason) => reason.type === 'language_assessment' && reason.required)
    : false
  const hasAssessmentStepCompleted = !isAccessRequestInfoLoading
    ? accessRequestInfo?.gated && !isUserWhitelisted
      ? accessRequestInfo?.requirements.languageAssessmentComplete
      : reasons.some((reason) => reason.type === 'language_assessment' && reason.completed)
    : false

  const pretestReason = reasons.find((reason) => reason.type === 'annotator_pretest')
  const hasVerificationStep = project.type === 'AUDIO_ANNOTATION' && !!pretestReason
  const verificationCompleted = pretestReason?.passed === true
  const verificationFailed = pretestReason?.completed === true && pretestReason?.passed !== true
  const [verificationOpen, setVerificationOpen] = useState(false)

  // Steps that must be done before the mission can start.
  const priorStepsCompleted = profileCompleted && (!hasAssessmentStep || assessmentCompleted)
  const canStartMission = priorStepsCompleted && (!hasVerificationStep || verificationCompleted)

  // Wizard numbering: Profile is always 1; assessment (optional) then verification (optional) follow.
  const verificationIndex = hasAssessmentStep ? 3 : 2
  const finalIndex = verificationIndex + (hasVerificationStep ? 1 : 0)

  const requiredLanguages = Array.from(
    new Set(reasons.filter((reason) => reason.type === 'profile_language' && reason.language).map((r) => r.language!))
  )
  const hasProfileLanguageReason = requiredLanguages.length > 0

  const upToUsd = `$${formatNumber(rewardConfig.amount, 2)}`
  const upToData = formatNumber(rewardConfig.amount, 0)

  const isInviteOnly = whitelistEnabled && !isUserWhitelisted

  const onInterestedClick = async () => {
    if (!isAuthorized || accessRequestInfo?.status !== 'NONE') return
    await requestAccess(projectId)
  }

  return (
    <div className="max-mobile:max-w-[calc(100vw-40px)] m-auto mt-12 flex w-[720px] flex-col">
      {rewardConfig.amount > 0 && (
        <div className="max-mobile:px-0 relative -mt-12 px-10">
          <div className="max-mobile:text-[22px]/[24px] max-mobile:py-4 max-mobile:pt-12 pt-9 pb-4 text-[28px]/[32px] font-semibold">
            {rewardConfig.type === 'usd' ? (
              <>
                <Trans>
                  Complete the mission{' '}
                  <span className="text-orange max-mobile:max-w-[200px] max-mobile:mt-2 block">
                    to earn up to {upToUsd}!
                  </span>
                </Trans>
                {estimatedMinutes && (
                  <div className="text-body-2 text-gray-90 max-mobile:max-w-[200px] max-mobile:mt-2 mt-8 font-medium!">
                    <Trans>Estimated time to complete:</Trans>{' '}
                    <span className="text-gray-80">
                      <Trans>{estimatedMinutes} minutes</Trans>
                    </span>
                  </div>
                )}
              </>
            ) : (
              <>
                <Trans>
                  Complete the mission to earn up to{' '}
                  <span className="text-orange max-mobile:max-w-[200px] max-mobile:mt-2 block">
                    {upToData} $DATA points for airdrop!
                  </span>
                </Trans>
                {estimatedMinutes && (
                  <div className="text-body-2 text-gray-90 max-mobile:max-w-[200px] max-mobile:mt-2 mt-8 font-medium!">
                    <Trans>Estimated time to complete:</Trans>{' '}
                    <span className="text-gray-80">
                      <Trans>{estimatedMinutes} minutes</Trans>
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
          {rewardConfig.type !== 'usd' ? (
            <img
              src="/images/dh-coins.png"
              role="presentation"
              className="max-mobile:w-[110px] max-mobile:right-4 absolute right-10 bottom-0 w-[155px]"
            />
          ) : (
            <img
              src="/images/usd-earnings.webp"
              role="presentation"
              className="max-mobile:w-[135px] max-mobile:bottom-[-40px] max-mobile:right-[-10px] absolute right-10 -bottom-14 -z-[1] w-[210px]"
            />
          )}
        </div>
      )}
      <div className="bg-background max-mobile:px-4 max-mobile:py-8 flex flex-col items-center justify-center gap-4 rounded-2xl border border-solid border-gray-100 px-10 py-12 text-center">
        {verificationFailed ||
        reasons.some(
          (reason) =>
            ['profile_language' /* , 'not_whitelisted' */].includes(reason.type ?? '') ||
            accessRequestInfo?.status === 'REJECTED' ||
            project.status === 'COMPLETED' ||
            (hasAssessmentStepCompleted ? reason.type === 'quota_unavailable' : false)
        ) ? (
          hasProfileLanguageReason && !isInviteOnly ? (
            <MissionLockedByLanguages
              projectId={projectId}
              title={title}
              description={description ?? ''}
              languages={requiredLanguages}
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              <div className="flex w-full flex-col items-center justify-center gap-2 border-b border-gray-100 pb-7">
                <div className="text-subtitle text-gray-80 font-semibold">{title}</div>
                <div className="text-body text-gray-80 max-w-[600px] whitespace-pre-line">{description}</div>
              </div>
              {verificationFailed ? (
                <>
                  <Text type="h5" className="text-gray-70 mt-2">
                    <Trans>Mission Not Available</Trans>
                  </Text>
                  <Text type="body1-regular" className="text-gray-80">
                    <Trans>
                      Based on your test results, you are not yet eligible to participate in annotation missions. Feel
                      free to try again later with another mission.
                    </Trans>
                  </Text>
                </>
              ) : isInviteOnly && accessRequestInfo?.status === 'REJECTED' ? (
                <>
                  <Text type="h5" className="text-gray-70 mt-2">
                    <Trans>Request to join the wait list was not approved</Trans>
                  </Text>
                  <Text type="body1-regular" className="text-gray-80">
                    <Trans>
                      Unfortunately, your request to join this mission was not approved. Thank you for your interest.
                    </Trans>
                  </Text>
                </>
              ) : (
                <>
                  <Text type="h5" className="text-gray-70 mt-2">
                    <Trans>Mission at Full Capacity</Trans>
                  </Text>
                  <div className="flex flex-col items-center justify-center">
                    <Text type="body1-regular" className="text-gray-80">
                      <Trans>
                        All participation for this mission has been allocated. Availability may change, so please check
                        again later.
                      </Trans>
                    </Text>
                  </div>
                </>
              )}
              <Button size="small" variant="bordered" onClick={() => navigate(missionsUrl)} className="mt-2">
                <Trans>Back to Missions</Trans>
              </Button>
            </div>
          )
        ) : (
          <>
            <div className="flex w-full flex-col items-center gap-3 border-b border-gray-100 pb-7 text-center">
              <div className="flex flex-col items-center justify-center gap-2">
                <Text type="h5" className="text-gray-70">
                  {title}
                </Text>
                <div className="text-body text-gray-80 max-w-[600px] whitespace-pre-line">{description}</div>
              </div>
              <Text type="body1-regular" className="text-gray-80 max-mobile:mt-4 mt-6">
                <Trans>Complete the steps below to unlock this mission.</Trans>
              </Text>
              {!isAuthorized && (
                <Button className="mt-2 w-[300px]" onClick={goToLogin}>
                  <Trans>Sign In</Trans>
                </Button>
              )}
            </div>
            <div className="mt-2 flex flex-col">
              <WizardStep
                projectId={projectId}
                index={1}
                title={t`User Profile`}
                description={t`Tell us a bit about yourself so we can match you with the right missions.`}
                completed={profileCompleted}
                active={!profileCompleted && isAuthorized}
                buttonLabel={t`Complete Profile`}
                buttonDisabled={!isAuthorized}
                onClick={goToProfile}
                buttonType="default"
              />
              {hasAssessmentStep && (
                <WizardStep
                  projectId={projectId}
                  index={2}
                  title={t`English Assessment`}
                  description={
                    <>
                      <Trans>Take a short English assessment to confirm your eligibility.</Trans>
                      {assessmentCompleted && assessmentFinalResult && (
                        <div className="mt-2">
                          <Trans>Your level:</Trans>{' '}
                          <span className="text-green font-semibold">{assessmentFinalResult.level}</span>
                        </div>
                      )}
                    </>
                  }
                  completed={assessmentCompleted}
                  active={profileCompleted && !assessmentCompleted}
                  buttonLabel={
                    assessmentMission?.view.latestSubmissionStatus === 'IN_PROGRESS'
                      ? t`Continue Assessment`
                      : t`Complete Assessment`
                  }
                  buttonDisabled={!isAuthorized || !profileCompleted}
                  buttonType="default"
                  onClick={goToAssessment}
                />
              )}
              {hasVerificationStep && (
                <WizardStep
                  projectId={projectId}
                  index={verificationIndex}
                  title={t`Annotator Verification`}
                  description={t`Pass a short check to confirm you're ready to annotate according to the requirements.`}
                  completed={verificationCompleted}
                  active={priorStepsCompleted && !verificationCompleted}
                  buttonLabel={t`Start Verification`}
                  buttonDisabled={!isAuthorized || !priorStepsCompleted}
                  buttonType="default"
                  onClick={() => setVerificationOpen(true)}
                />
              )}
              {isInviteOnly ? (
                <WizardStep
                  projectId={projectId}
                  index={finalIndex}
                  title={t`Join Waitlist`}
                  description={
                    <Text type="body1-regular" className="text-gray-80">
                      {accessRequestInfo?.status === 'PENDING' ? (
                        <Trans>
                          You are on the waitlist for this mission. You will be notified when it becomes available.
                        </Trans>
                      ) : (
                        <Trans>This mission requires whitelist approval. Apply now for a chance to participate.</Trans>
                      )}
                    </Text>
                  }
                  active={canStartMission}
                  completed={accessRequestInfo?.status === 'PENDING'}
                  completedLabel={accessRequestInfo?.status === 'PENDING' ? t`You've sent your request` : undefined}
                  buttonType="default"
                  buttonLabel={
                    accessRequestInfo?.status === 'NONE' ? t`I'm interested in this mission` : t`Request sent`
                  }
                  buttonDisabled={
                    !isAuthorized ||
                    !profileCompleted ||
                    !assessmentCompleted ||
                    (hasVerificationStep && !verificationCompleted)
                  }
                  buttonLoading={isRequestingAccess}
                  onClick={onInterestedClick}
                />
              ) : (
                <WizardStep
                  projectId={projectId}
                  index={finalIndex}
                  title={t`Start Mission`}
                  description={
                    <EnrollmentContent disabled={!canStartMission} projectId={projectId} rules={rules ?? ''} />
                  }
                  completed={false}
                  active={canStartMission}
                  buttonType="none"
                />
              )}
            </div>
          </>
        )}
      </div>
      {hasVerificationStep && (
        <AnnotatorVerificationDialog
          open={verificationOpen}
          onClose={() => setVerificationOpen(false)}
          projectId={projectId}
        />
      )}
    </div>
  )
}
